/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'

import { DecoratorNode } from 'lexical'
import * as React from 'react'
import { Suspense } from 'react'

const ExcalidrawComponent = React.lazy(
  // @ts-ignore
  () => import('./ExcalidrawComponent'),
)

export type SerializedExcalidrawNode = Spread<
  {
    data: string
  },
  SerializedLexicalNode
>

function convertExcalidrawElement(
  domNode: HTMLElement,
): DOMConversionOutput | null {
  const excalidrawData = domNode.getAttribute('data-lexical-excalidraw-json')
  if (excalidrawData) {
    const node = $createExcalidrawNode()
    node.__data = excalidrawData
    return {
      node,
    }
  }
  return null
}

export class ExcalidrawNode extends DecoratorNode<React.JSX.Element> {
  __data: string

  static getType(): string {
    return 'excalidraw'
  }

  static clone(node: ExcalidrawNode): ExcalidrawNode {
    return new ExcalidrawNode(node.__data, node.__key)
  }

  static importJSON(serializedNode: SerializedExcalidrawNode): ExcalidrawNode {
    return new ExcalidrawNode(serializedNode.data)
  }

  exportJSON(): SerializedExcalidrawNode {
    return {
      data: this.__data,
      type: 'excalidraw',
      version: 1,
    }
  }

  constructor(data = '[]', key?: NodeKey) {
    super(key)
    this.__data = data
  }

  // View
  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span')
    const theme = config.theme
    const className = theme.image
    if (className !== undefined) {
      span.className = className
    }
    return span
  }

  updateDOM(): false {
    return false
  }

  static importDOM(): DOMConversionMap<HTMLSpanElement> | null {
    return {
      span: (domNode: HTMLSpanElement) => {
        if (!domNode.hasAttribute('data-lexical-excalidraw-json')) {
          return null
        }
        return {
          conversion: convertExcalidrawElement,
          priority: 1,
        }
      },
    }
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('span')
    const content = editor.getElementByKey(this.getKey())
    if (content !== null) {
      const svg = content.querySelector('svg')
      if (svg !== null) {
        element.innerHTML = svg.outerHTML
      }
    }
    element.setAttribute('data-lexical-excalidraw-json', this.__data)
    return { element }
  }

  setData(data: string): void {
    const self = this.getWritable()
    self.__data = data
  }

  decorate(editor: LexicalEditor, config: EditorConfig): React.JSX.Element {
    return (
      <Suspense fallback={null}>
        <ExcalidrawComponent nodeKey={this.getKey()} data={this.__data} />
      </Suspense>
    )
  }
}

export function $createExcalidrawNode(): ExcalidrawNode {
  return new ExcalidrawNode()
}

export function $isExcalidrawNode(
  node: LexicalNode | null,
): node is ExcalidrawNode {
  return node instanceof ExcalidrawNode
}
